import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/firestore';
import { countAllowanceSessions } from '../utils/testerName';
import { getSeenAt, countNew, SEEN_EVENT } from '../utils/sessionsSeen';

/**
 * useAeroShellData: the small amount of data the v3 app shell shows on every
 * Aero page (sidebar counts, recent tests, the plan meter, the account).
 *
 * Reads only what the app already reads elsewhere, with the same queries:
 *   - user/{uid}/url (one collection read), the same filters as the Sites list
 *     (AeroSites isAeroSiteDoc) and the Tests list (useTestingProjects);
 *   - the testingSessions size per test, the same count Account.js shows as
 *     "N of M" (SESSION_LIMIT_MODE 'total': sessions stored);
 *   - user/{uid} for the name and email, as the sidebar did before.
 * The url list is re-read when the route changes (so a new or deleted site or
 * test shows up after navigating); the session count only when the set of
 * tests changes. Every read is best effort: a failure leaves the value null and
 * the shell simply shows less.
 *
 * The same session read also gives each test its session count and how many
 * are new since the owner last opened its Sessions tab (utils/sessionsSeen),
 * for the sidebar's recent tests: `sessionInfo` = { [testId]: { total, new } }.
 * Only the few fields needed are kept from each session, not the transcript.
 */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);

// A test copy (sourceSiteId, see UserTesting/testCopy.js) is a test, not a
// site: it is left out of the site rows and counts.
export const isAeroSiteDoc = (s) => !s.sourceSiteId && (s.type === 'html' || (s.type === 'figma' && s.aeroSite));

const isTestDoc = (d) => {
  const ut = d.userTesting;
  const configured = ut && (ut.enabled === true || ut.status);
  const testDraft = d.draft === true && !!ut;
  return !!(configured || testDraft);
};

export default function useAeroShellData(uid, pathname) {
  const [account, setAccount] = useState({ name: '', email: '' });
  const [sites, setSites] = useState(null);
  const [tests, setTests] = useState(null);
  const [sessionsUsed, setSessionsUsed] = useState(null);
  const [sessionLists, setSessionLists] = useState(null);
  const [seenTick, setSeenTick] = useState(0);
  const [failed, setFailed] = useState(false);
  const testRefsRef = useRef([]);
  const [testKey, setTestKey] = useState('');

  useEffect(() => {
    if (!uid) return undefined;
    let cancelled = false;
    firebase.firestore().collection('user').doc(uid).get()
      .then((doc) => {
        if (cancelled || !doc.exists) return;
        const d = doc.data();
        setAccount({ name: d.name || '', email: d.email || '' });
      })
      .catch((err) => console.error('Error fetching user:', err));
    return () => { cancelled = true; };
  }, [uid]);

  const loadUrls = useCallback(async () => {
    if (!uid) return;
    try {
      const snap = await firebase.firestore().collection('user').doc(uid).collection('url').get();
      const docs = snap.docs.map((d) => ({ id: d.id, ref: d.ref, ...d.data() }));
      const siteRows = docs
        .filter(isAeroSiteDoc)
        .map((s) => ({
          id: s.id,
          title: s.title || '',
          generatedUrl: s.generatedUrl,
          type: s.type,
          customDomain: s.customDomain,
          activeVersion: s.activeVersion || null,
          urls: s.urls || null,
          draft: s.draft === true && !s.activeVersion,
          isPasswordActive: !!s.isPasswordActive,
          faviconUrl: s.faviconUrl || null,
          previewUrl: s.previewUrl || null,
          previewColors: Array.isArray(s.previewColors) ? s.previewColors : null,
          createdAt: toDate(s.createdAt),
          updatedAt: toDate(s.updatedAt),
        }));
      const testDocs = docs.filter(isTestDoc);
      const testRows = testDocs.map((t) => ({
        id: t.id,
        title: t.title || 'Untitled Project',
        // A configured test is never a draft (the same heal useTestingProjects applies).
        draft: t.draft === true && !(t.userTesting && t.userTesting.enabled === true),
        status: t.userTesting?.status || null,
        targetSessions: Number(t.userTesting?.targetSessions) || 0,
        createdAt: toDate(t.createdAt),
        updatedAt: toDate(t.updatedAt),
      }));
      testRefsRef.current = testDocs.filter((t) => !testRows.find((r) => r.id === t.id)?.draft).map((t) => t.ref);
      setFailed(false);
      setSites(siteRows);
      setTests(testRows);
      setTestKey(testRefsRef.current.map((r) => r.id).sort().join(','));
    } catch (err) {
      console.error('Error loading shell data:', err);
      setFailed(true);
    }
  }, [uid]);

  useEffect(() => {
    loadUrls();
  }, [loadUrls, pathname]);

  useEffect(() => {
    if (!uid || tests === null) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const snaps = await Promise.all(
          testRefsRef.current.map((ref) => ref.collection('testingSessions').get())
        );
        if (cancelled) return;
        setSessionsUsed(snaps.map(countAllowanceSessions).reduce((a, b) => a + b, 0));
        const lists = {};
        snaps.forEach((snap, i) => {
          lists[testRefsRef.current[i].id] = snap.docs.map((d) => {
            const x = d.data();
            return {
              status: x.status || null,
              startedAt: x.startedAt || null,
              completedAt: x.completedAt || null,
              isOwnerPreview: x.isOwnerPreview === true,
            };
          });
        });
        setSessionLists(lists);
      } catch (err) {
        console.error('Error counting sessions:', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, testKey, tests === null]);

  // Opening a test's Sessions tab marks it seen; recount the "new" marks.
  useEffect(() => {
    const on = () => setSeenTick((n) => n + 1);
    window.addEventListener(SEEN_EVENT, on);
    return () => window.removeEventListener(SEEN_EVENT, on);
  }, []);

  const sessionInfo = useMemo(() => {
    if (!sessionLists) return null;
    const out = {};
    Object.keys(sessionLists).forEach((id) => {
      const list = sessionLists[id];
      out[id] = { total: countAllowanceSessions(list), new: countNew(list, getSeenAt(uid, id)) };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionLists, uid, seenTick]);

  return { account, sites, tests, sessionsUsed, sessionInfo, failed, refresh: loadUrls };
}
